/**
 * Input normalization for route stop operations.
 *
 * Converts single stops, bulk row arrays, and explicit-shape objects
 * into a consistent StopDescriptor[] so all downstream payload builders
 * and pipeline builders work on the same shape.
 */
import { isOnRequestStop } from 'utils/route'

/**
 * @typedef {Object} StopDescriptor
 * @property {string} workOrderId - daily identifier
 * @property {string|null} configuredServiceEventId - master identifier (null for on-request / missing)
 * @property {string|null} routeId - source route (for move detection)
 * @property {boolean} isOnRequest - true if on-request service (excluded from master ops)
 * @property {string|null} date - FE format date (for between-days moves)
 */

/**
 * Normalizes a single stop or array of stops into a consistent StopDescriptor[].
 *
 * Accepts two input shapes:
 *   1. Row shape (from grids/dialogs): { id, configuredServiceEventId, routeId, pricedServiceType, ... }
 *   2. Explicit shape (from hooks): { workOrderId, configuredServiceEventId, routeId, ... }
 *
 * @param {Object|Object[]} input - single stop object or array of stop objects
 * @returns {StopDescriptor[]}
 */
export const normalizeStops = input => {
  if (!input) return []
  const items = Array.isArray(input) ? input : [input]
  return items.filter(Boolean).map(item => ({
    workOrderId: item.workOrderId || item.id || null,
    configuredServiceEventId: item.configuredServiceEventId || null,
    routeId: item.routeId || null,
    isOnRequest: isOnRequestStop(item),
    date: item.date || null,
  }))
}

/**
 * Extracts workOrderIds from normalized stops, filtering falsy values.
 * @param {StopDescriptor[]} stops
 * @returns {string[]}
 */
export const extractWorkOrderIds = stops => stops.map(s => s.workOrderId).filter(Boolean)

/**
 * Extracts configuredServiceEventIds eligible for master operations.
 * Excludes on-request stops and stops without a CSE ID.
 * @param {StopDescriptor[]} stops
 * @returns {string[]}
 */
export const extractMasterEligibleCseIds = stops =>
  stops.filter(s => !s.isOnRequest && s.configuredServiceEventId).map(s => s.configuredServiceEventId)

/**
 * Returns only stops that originated from a different route than the destination.
 * Used to identify stops that were actually moved/assigned (not already on the destination route).
 *
 * Replaces getNewlyAssignedRows from settings.js
 *
 * @param {StopDescriptor[]} stops
 * @param {string} destinationRouteId
 * @returns {StopDescriptor[]}
 */
export const getNewlyAssignedStops = (stops, destinationRouteId) => stops.filter(s => s.routeId !== destinationRouteId)

/**
 * Returns only stops that were moved from a specific date.
 * Used by permanent recurrence change (between-days moves).
 *
 * Replaces getMovedRowsByDate from settings.js
 *
 * @param {StopDescriptor[]} stops
 * @param {string} fromDate
 * @returns {StopDescriptor[]}
 */
export const getMovedStopsByDate = (stops, fromDate) => stops.filter(s => s.date === fromDate)
